/**
 * useResizableBox — drag-to-resize state for the Compact VQE popover.
 *
 * The popover opens at its CSS default size (`size === null` → the caller keeps
 * its `max-h-[70vh] w-[min(32rem,90vw)]` classes). The first drag seeds explicit
 * pixel width/height from the live box rect, then subsequent moves adjust them,
 * clamped to a sane min and a viewport-relative max. `reset()` drops back to the
 * CSS default (the popover calls it on close so a re-open starts fresh).
 *
 * Drag tracking uses window-level `pointermove`/`pointerup` listeners (not
 * pointer capture) so a fast drag that leaves the thin handle keeps resizing —
 * the same robust pattern as the Browser-mode shell resize.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface BoxSize {
  readonly width: number;
  readonly height: number;
}

export type ResizeAxis = 'x' | 'y' | 'xy';

const MIN_WIDTH = 320;
const MIN_HEIGHT = 240;
const MAX_WIDTH_FRAC = 0.95;
const MAX_HEIGHT_FRAC = 0.9;

export interface ResizableBox {
  /** Explicit pixel size once the user has dragged, else null (CSS default). */
  readonly size: BoxSize | null;
  /** Ref to attach to the resizable element (its rect seeds the first drag). */
  readonly boxRef: React.RefObject<HTMLDivElement>;
  /** Pointer-down handler factory for a handle on the given axis/axes. */
  readonly startResize: (axis: ResizeAxis) => (e: React.PointerEvent) => void;
  /** Drop back to the CSS default size. */
  readonly reset: () => void;
}

interface DragState {
  readonly axis: ResizeAxis;
  readonly startX: number;
  readonly startY: number;
  readonly startW: number;
  readonly startH: number;
}

/**
 * @param onResizeEnd Fired once per completed drag (for analytics). Receives the
 *   axis that was dragged.
 */
export function useResizableBox(onResizeEnd?: (axis: ResizeAxis) => void): ResizableBox {
  const [size, setSize] = useState<BoxSize | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const movedRef = useRef(false);
  // Keep the latest callback without re-binding the window listeners each render.
  const onEndRef = useRef(onResizeEnd);
  onEndRef.current = onResizeEnd;

  const handleMove = useCallback((e: PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    movedRef.current = true;
    const maxW = window.innerWidth * MAX_WIDTH_FRAC;
    const maxH = window.innerHeight * MAX_HEIGHT_FRAC;
    let width = drag.startW;
    let height = drag.startH;
    if (drag.axis === 'x' || drag.axis === 'xy') {
      width = Math.min(maxW, Math.max(MIN_WIDTH, drag.startW + (e.clientX - drag.startX)));
    }
    if (drag.axis === 'y' || drag.axis === 'xy') {
      height = Math.min(maxH, Math.max(MIN_HEIGHT, drag.startH + (e.clientY - drag.startY)));
    }
    setSize({ width, height });
  }, []);

  const handleUp = useCallback(() => {
    const drag = dragRef.current;
    dragRef.current = null;
    window.removeEventListener('pointermove', handleMove);
    window.removeEventListener('pointerup', handleUp);
    if (drag && movedRef.current) onEndRef.current?.(drag.axis);
  }, [handleMove]);

  const startResize = useCallback(
    (axis: ResizeAxis) => (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const rect = boxRef.current?.getBoundingClientRect();
      if (!rect) return;
      dragRef.current = {
        axis,
        startX: e.clientX,
        startY: e.clientY,
        startW: rect.width,
        startH: rect.height,
      };
      movedRef.current = false;
      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
    },
    [handleMove, handleUp],
  );

  const reset = useCallback(() => setSize(null), []);

  // Safety net: drop listeners if the component unmounts mid-drag.
  useEffect(
    () => () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    },
    [handleMove, handleUp],
  );

  return { size, boxRef, startResize, reset };
}
